import { z } from "zod";
import { HumanSupportScreenshotSchema, SUPPORT_SCREENSHOT_LIMIT } from "./support-screenshots";

export const CommercialReadinessCheckSchema = z.object({
  key: z.string(),
  label: z.string(),
  ready: z.boolean(),
  blocking: z.boolean(),
  detail: z.string(),
});
export const CommercialReadinessSchema = z.object({
  environment: z.enum(["development", "test", "production"]),
  readyForProduction: z.boolean(),
  checkedAt: z.string(),
  checks: z.array(CommercialReadinessCheckSchema),
});
export type CommercialReadiness = z.infer<typeof CommercialReadinessSchema>;

export const ProductionVerificationKeySchema = z.enum(["sms.aliyun", "payment.wechat", "payment.alipay", "legal.policies"]);
export const RecordProductionVerificationRequestSchema = z.object({
  evidenceRef: z.string().trim().min(3).max(1000),
  expiresAt: z.string().datetime(),
});
export type ProductionVerificationKey = z.infer<typeof ProductionVerificationKeySchema>;

export const HumanSupportContextSchema = z.object({
  summary: z.string().trim().min(2).max(2000),
  platform: z.string().trim().max(80).optional(),
  taskId: z.string().max(120).optional(),
  conversationId: z.string().max(120).optional(),
  pageUrl: z.string().url().max(1200).optional(),
  shareConversation: z.boolean().default(false),
  shareTask: z.boolean().default(false),
  sharePage: z.boolean().default(false),
  screenshots: z.array(HumanSupportScreenshotSchema).max(SUPPORT_SCREENSHOT_LIMIT).optional(),
  shareScreenshots: z.boolean().optional(),
}).superRefine((value, ctx) => {
  if (value.screenshots?.length && value.shareScreenshots !== true) ctx.addIssue({ code: "custom", path: ["shareScreenshots"], message: "请先确认截图共享范围" });
  if (new Set(value.screenshots?.map(image => image.id)).size !== (value.screenshots?.length || 0)) ctx.addIssue({ code: "custom", path: ["screenshots"], message: "截图重复" });
});
export type HumanSupportContext = z.infer<typeof HumanSupportContextSchema>;
export const HumanSupportWriteContextSchema = HumanSupportContextSchema.refine(value => value.screenshots?.every(image => image.dataUrl) ?? true, "截图未完整加载，请重试后保存");

export const HumanSupportDraftSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["draft", "converted", "expired"]),
  context: HumanSupportContextSchema,
  createdAt: z.string(),
  expiresAt: z.string(),
  orderNo: z.string().nullable().optional(),
});
export type HumanSupportDraft = z.infer<typeof HumanSupportDraftSchema>;

export const AttachHumanSupportDraftRequestSchema = z.object({ draftId: z.string().uuid() });

export const UpdateHumanSupportContextRequestSchema = HumanSupportContextSchema;
export const HumanSupportStatusSchema = z.enum(["waiting", "in_progress", "completed", "refunding", "refunded"]);
export const HumanSupportTicketSchema = z.object({
  serviceNo: z.string(),
  orderNo: z.string(),
  status: HumanSupportStatusSchema,
  qrImageUrl: z.string().nullable(),
  context: HumanSupportContextSchema.nullable(),
  operatorNote: z.string().nullable(),
  resolution: z.string().nullable(),
  knowledgeCandidateId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type HumanSupportTicket = z.infer<typeof HumanSupportTicketSchema>;

export const UpdateHumanSupportTicketRequestSchema = z.object({
  status: z.enum(["in_progress", "completed", "refunding", "refunded"]),
  operatorNote: z.string().trim().max(2000).optional(),
  resolution: z.string().trim().max(4000).optional(),
  reusableKnowledge: z.boolean().default(false),
}).superRefine((value, context) => {
  if (value.status === "completed" && !value.resolution) context.addIssue({ code: "custom", path: ["resolution"], message: "完成工单必须填写处理结果" });
});

export const ReconciliationSummarySchema = z.object({
  from: z.string(),
  to: z.string(),
  orderCount: z.number().int().nonnegative(),
  paidCents: z.number().int().nonnegative(),
  refundedCents: z.number().int().nonnegative(),
  netCents: z.number().int(),
  unsettledPaymentCount: z.number().int().nonnegative(),
  abnormalRefundCount: z.number().int().nonnegative(),
  estimatedModelCostMicros: z.number().int().nonnegative(),
  generatedAt: z.string(),
});
export type ReconciliationSummary = z.infer<typeof ReconciliationSummarySchema>;
